/**
 * Editor do Mapa Unik — Powered by Astral MindMeister Engine.
 * Experiência cinematográfica e fluida com:
 * - Canvas vetorial reativo com estimativa de texto e barreira anti-colisão
 * - 3 Modos de Layout (Mapa Radial, Organograma Hierárquico, Outline Lista)
 * - 5 Formatos de cards (Arredondado, Retângulo, Sublinhado, Cápsula, Notion Card)
 * - 9 Temas cromáticos luxuosos (Sellflux, Obsidian Dark, Meister Claro, Ocean, Sunset, etc)
 * - Reparenting com pré-visualização de curvas Bézier tracejadas
 * - Atalhos de teclado (Tab filho, Enter irmão, F2 editar, Delete excluir)
 * - Auto-save em tempo real (700ms debounce) com BroadcastChannel multi-abas
 * - Histórico de versões e snapshots com restauração segura
 * - Exportação em PNG Retina 2x e JSON
 * - Modo Tela Cheia Zen
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  Palette,
  LayoutTemplate,
  Share2,
  Clock,
  RotateCcw,
  RotateCw,
  Maximize2,
  Minimize2,
  Check,
  Sparkles,
  X,
  FileDown,
  Camera,
} from "lucide-react";
import { api } from "../../lib/api";
import { AstralCanvas } from "../../components/astral/AstralCanvas";
import { AstralAppearanceDrawer } from "../../components/astral/AstralAppearanceDrawer";
import { AstralTemplatesModal } from "../../components/astral/AstralTemplatesModal";
import { AstralShareModal } from "../../components/astral/AstralShareModal";
import type {
  AstralMapData,
  AstralLayout,
  AstralThemeId,
} from "../../types/astral";
import type { AstralTemplate } from "../../data/astralTemplates";

interface SnapshotItem {
  id: string;
  mapId: string;
  label: string;
  nodeCount: number;
  createdAt: string;
  createdBy: string | null;
}

export function Mapa() {
  const { id: paramClientId, mapId: paramMapId } = useParams();
  const effectiveMapId = paramMapId || paramClientId || "";
  const navigate = useNavigate();

  // Estados do Mapa e Cliente
  const [clientId, setClientId] = useState<string | null>(paramClientId && paramMapId ? paramClientId : null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [title, setTitle] = useState("Mapa Mental");
  const [activeMap, setActiveMap] = useState<AstralMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: "info" | "success" | "error" } | null>(null);

  // Status de Salvamento e Sincronização
  const [savingStatus, setSavingStatus] = useState<"saved" | "saving" | "error" | "live_updated">("saved");

  // Modais e Gavetas
  const [isAppearanceOpen, setIsAppearanceOpen] = useState(false);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [isSnapshotsOpen, setIsSnapshotsOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Histórico de Versões / Snapshots
  const [snapshots, setSnapshots] = useState<SnapshotItem[]>([]);
  const [versionLabel, setVersionLabel] = useState("");
  const [isSavingSnapshot, setIsSavingSnapshot] = useState(false);

  // Histórico Local Undo / Redo
  const [history, setHistory] = useState<AstralMapData[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Referências para Auto-save e BroadcastChannel
  const autoSaveTimerRef = useRef<any>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastSavedDataStringRef = useRef<string>("");

  function showToast(message: string, type: "info" | "success" | "error" = "info") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  // Carrega histórico de versões
  const loadSnapshots = useCallback(async () => {
    if (!effectiveMapId) return;
    try {
      const list = await api.get<SnapshotItem[]>(`/maps/${effectiveMapId}/snapshots`);
      setSnapshots(list || []);
    } catch {
      setSnapshots([]);
    }
  }, [effectiveMapId]);

  // Carrega o mapa inicial
  useEffect(() => {
    if (!effectiveMapId) return;

    let isMounted = true;
    setLoading(true);

    api
      .get<{
        id: string;
        clientId?: string;
        clientName?: string;
        title: string;
        layout?: string;
        theme?: string;
        data?: AstralMapData;
        nodes?: any[];
      }>(`/maps/${effectiveMapId}`)
      .then((res) => {
        if (!isMounted) return;
        setTitle(res.title || "Mapa Mental");
        if (res.clientId) {
          setClientId(res.clientId);
          setClientName(res.clientName || null);
        }

        const mapData: AstralMapData = res.data || {
          rootId: "root_base",
          layout: (res.layout as AstralLayout) || "map",
          theme: (res.theme as AstralThemeId) || "sellflux",
          viewport: { zoom: 1, panX: 0, panY: 0 },
          nodes: {
            root_base: {
              id: "root_base",
              parentId: null,
              text: res.title || "Mapa Mental",
              side: "right",
              order: 0,
              isCollapsed: false,
            },
          },
        };

        lastSavedDataStringRef.current = JSON.stringify(mapData);
        setActiveMap(mapData);
        setHistory([mapData]);
        setHistoryIndex(0);
        void loadSnapshots();
      })
      .catch(() => {
        if (isMounted) setNotFound(true);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [effectiveMapId, loadSnapshots]);

  // BroadcastChannel para sincronização instantânea (0ms) entre abas do mesmo navegador
  useEffect(() => {
    if (!effectiveMapId) return;
    try {
      if (typeof window !== "undefined" && "BroadcastChannel" in window) {
        channelRef.current = new BroadcastChannel("unik_astral_sync");
        channelRef.current.onmessage = (event) => {
          if (event.data?.type === "MAP_UPDATED" && event.data?.mapId === effectiveMapId) {
            if (event.data?.data) {
              const remoteData = event.data.data as AstralMapData;
              if (JSON.stringify(remoteData) !== lastSavedDataStringRef.current) {
                lastSavedDataStringRef.current = JSON.stringify(remoteData);
                setActiveMap(remoteData);
                setSavingStatus("live_updated");
                setTimeout(() => setSavingStatus("saved"), 3000);
              }
            }
          }
        };
      }
    } catch {
      channelRef.current = null;
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [effectiveMapId]);

  // Função central de atualização de mapa com auto-save
  const handleUpdateMapData = useCallback(
    (newData: AstralMapData, addToHistory = true) => {
      setActiveMap(newData);

      if (addToHistory) {
        setHistory((prev) => {
          const upToCurrent = prev.slice(0, historyIndex + 1);
          return [...upToCurrent, newData];
        });
        setHistoryIndex((prev) => prev + 1);
      }

      setSavingStatus("saving");

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      autoSaveTimerRef.current = setTimeout(async () => {
        if (!effectiveMapId) return;
        try {
          await api.put(`/maps/${effectiveMapId}`, {
            layout: newData.layout,
            theme: newData.theme,
            data_json: newData,
          });

          lastSavedDataStringRef.current = JSON.stringify(newData);
          setSavingStatus("saved");

          // Notifica outras abas locais via BroadcastChannel
          try {
            if (channelRef.current) {
              channelRef.current.postMessage({
                type: "MAP_UPDATED",
                mapId: effectiveMapId,
                data: newData,
              });
            }
          } catch {
            // ignore
          }
        } catch {
          setSavingStatus("error");
        }
      }, 700);
    },
    [effectiveMapId, historyIndex],
  );

  // Undo / Redo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevData = history[historyIndex - 1];
      if (prevData) {
        setHistoryIndex(historyIndex - 1);
        handleUpdateMapData(prevData, false);
      }
    }
  }, [history, historyIndex, handleUpdateMapData]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextData = history[historyIndex + 1];
      if (nextData) {
        setHistoryIndex(historyIndex + 1);
        handleUpdateMapData(nextData, false);
      }
    }
  }, [history, historyIndex, handleUpdateMapData]);

  // Atalhos de teclado globais (Ctrl+Z, Ctrl+Y, F)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT") return;
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z" || e.key === "Z") {
          e.preventDefault();
          if (e.shiftKey) handleRedo();
          else handleUndo();
        } else if (e.key === "y" || e.key === "Y") {
          e.preventDefault();
          handleRedo();
        }
      } else if (e.key === "f" || e.key === "F") {
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          setIsFullscreen((prev) => !prev);
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleUndo, handleRedo]);

  // Alteração de título do mapa
  const handleTitleChange = async (newTitle: string) => {
    setTitle(newTitle);
    if (!effectiveMapId || !activeMap) return;

    const rootNode = activeMap.nodes[activeMap.rootId];
    if (rootNode) {
      const updatedMap = {
        ...activeMap,
        nodes: {
          ...activeMap.nodes,
          [activeMap.rootId]: { ...rootNode, text: newTitle },
        },
      };
      handleUpdateMapData(updatedMap);
    }

    try {
      await api.patch(`/maps/${effectiveMapId}`, { title: newTitle });
    } catch {
      // ignore
    }
  };

  // Aplicação de Modelo / Template
  const handleSelectTemplate = async (template: AstralTemplate) => {
    if (!activeMap) return;
    const templateData: AstralMapData = {
      ...template.data,
      viewport: activeMap.viewport,
    };
    handleUpdateMapData(templateData);
    setIsTemplatesModalOpen(false);
    showToast(`Modelo "${template.title}" aplicado com sucesso!`, "success");
  };

  // Geração / Obtenção de Link Compartilhável
  const handleOpenShare = () => {
    setIsShareModalOpen(true);
  };

  // Exportação em PNG Retina 2x
  const handleExportPng = () => {
    if (!activeMap) return;
    showToast("📷 Gerando exportação em alta resolução...");

    const posArray = Object.values(activeMap.nodes);
    if (posArray.length === 0) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = 1920;
    const height = 1080;
    const scale = 2;

    canvas.width = width * scale;
    canvas.height = height * scale;
    ctx.scale(scale, scale);

    // Fundo
    ctx.fillStyle = "#0b0f19";
    ctx.fillRect(0, 0, width, height);

    // Marca d'água Unik
    ctx.fillStyle = "rgba(255, 255, 255, 0.08)";
    ctx.font = "bold 24px sans-serif";
    ctx.fillText("UNIK ASTRAL MAP", 40, 50);

    ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
    ctx.font = "bold 32px sans-serif";
    ctx.fillText(title, 40, 100);

    ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
    ctx.font = "16px sans-serif";
    ctx.fillText(`Exportado em ${new Date().toLocaleDateString("pt-BR")}`, 40, 130);

    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${title.replace(/\s+/g, "_")}_astral.png`;
    a.click();
    showToast("Imagem PNG baixada com sucesso!", "success");
  };

  // Exportação em JSON
  const handleExportJson = () => {
    if (!activeMap) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(activeMap, null, 2));
    const a = document.createElement("a");
    a.href = dataStr;
    a.download = `${title.replace(/\s+/g, "_")}.astral.json`;
    a.click();
    showToast("Arquivo JSON baixado com sucesso!", "success");
  };

  // Salvar Snapshot / Versão manual
  const handleCreateSnapshot = async () => {
    if (!versionLabel.trim() || !effectiveMapId) return;
    try {
      setIsSavingSnapshot(true);
      await api.post(`/maps/${effectiveMapId}/snapshots`, { label: versionLabel.trim() });
      setVersionLabel("");
      await loadSnapshots();
      showToast("Versão salva com sucesso!", "success");
    } catch {
      showToast("Erro ao criar versão.", "error");
    } finally {
      setIsSavingSnapshot(false);
    }
  };

  // Restaurar Versão
  const handleRestoreSnapshot = async (snapshotId: string) => {
    if (!confirm("Restaurar esta versão substituirá o mapa atual. Continuar?")) return;
    try {
      await api.post(`/maps/${effectiveMapId}/snapshots/${snapshotId}/restore`);
      showToast("Versão restaurada!", "success");
      setIsSnapshotsOpen(false);
      window.location.reload();
    } catch {
      showToast("Erro ao restaurar versão.", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex h-dvh w-screen flex-col items-center justify-center bg-[#0b0e14] text-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-indigo-500" />
        <span className="mt-3 text-sm text-white/50">Carregando motor Astral...</span>
      </div>
    );
  }

  if (notFound || !activeMap) {
    return (
      <div className="flex h-dvh w-screen flex-col items-center justify-center bg-[#0b0e14] text-white">
        <h2 className="text-xl font-bold">Mapa Mental Não Encontrado</h2>
        <p className="mt-2 text-sm text-white/50">O mapa que você está tentando acessar não existe ou foi excluído.</p>
        <button
          type="button"
          onClick={() => navigate(clientId ? `/clientes/${clientId}/mapas` : "/mapas")}
          className="mt-6 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Voltar aos Mapas
        </button>
      </div>
    );
  }

  return (
    <div
      className={`relative flex h-dvh w-screen flex-col overflow-hidden bg-[#0b0e14] ${
        isFullscreen ? "fixed inset-0 z-[9999]" : ""
      }`}
    >
      {/* ------------------------------------------------------------- */}
      {/* BARRA SUPERIOR MINDMEISTER / ASTRAL TOOLBAR */}
      {/* ------------------------------------------------------------- */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 bg-[#111622]/95 px-4 backdrop-blur-md z-40">
        {/* Esquerda: Voltar + Título do Mapa */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(clientId ? `/clientes/${clientId}/mapas` : "/mapas")}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-all"
            title="Voltar"
          >
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">Voltar</span>
          </button>

          <div className="h-4 w-px bg-white/10" />

          <div className="flex items-center gap-2 min-w-0">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => handleTitleChange(e.target.value)}
              className="max-w-[160px] sm:max-w-[260px] md:max-w-[340px] truncate rounded-md bg-transparent px-2 py-1 text-sm font-bold text-white outline-none hover:bg-white/5 focus:bg-white/10 focus:ring-1 focus:ring-indigo-500"
              title="Clique para renomear o mapa"
            />
            {clientName && (
              <span className="hidden xl:inline text-xs text-white/40">({clientName})</span>
            )}

            {/* Status de Sincronização */}
            <div className="flex items-center gap-1.5 text-xs shrink-0">
              {savingStatus === "saving" && (
                <span className="flex items-center gap-1 text-indigo-400">
                  <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-400" />
                  <span className="hidden md:inline text-[11px]">Salvando...</span>
                </span>
              )}
              {savingStatus === "saved" && (
                <span className="flex items-center gap-1 text-emerald-400">
                  <Check size={12} />
                  <span className="hidden md:inline text-[11px]">Salvo</span>
                </span>
              )}
              {savingStatus === "live_updated" && (
                <span className="flex items-center gap-1 text-purple-400">
                  <Sparkles size={12} />
                  <span className="hidden md:inline text-[11px]">Sincronizado</span>
                </span>
              )}
              {savingStatus === "error" && (
                <span className="flex items-center gap-1 text-rose-400">
                  <span className="text-[11px]">Erro ao salvar</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Direita: Ações e Ferramentas */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Undo / Redo */}
          <button
            type="button"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Desfazer (Ctrl+Z)"
          >
            <RotateCcw size={14} />
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Refazer (Ctrl+Y)"
          >
            <RotateCw size={14} />
          </button>

          <div className="hidden sm:block h-4 w-px bg-white/10" />

          {/* Gaveta de Estilos & Aparência */}
          <button
            type="button"
            onClick={() => setIsAppearanceOpen(!isAppearanceOpen)}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-all ${
              isAppearanceOpen
                ? "border-indigo-500 bg-indigo-500/20 text-white"
                : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white"
            }`}
            title="Aparência, Temas e Formatos"
          >
            <Palette size={14} className="text-indigo-400" />
            <span className="hidden md:inline">Estilo & Layout</span>
          </button>

          {/* Modal de Modelos */}
          <button
            type="button"
            onClick={() => setIsTemplatesModalOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/80 hover:bg-white/10 hover:text-white transition-all"
            title="Aplicar Modelos Validados"
          >
            <LayoutTemplate size={14} className="text-amber-400" />
            <span className="hidden md:inline">Modelos</span>
          </button>

          {/* Histórico de Versões */}
          <button
            type="button"
            onClick={() => setIsSnapshotsOpen(!isSnapshotsOpen)}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-all ${
              isSnapshotsOpen
                ? "border-indigo-500 bg-indigo-500/20 text-white"
                : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white"
            }`}
            title="Histórico de Versões"
          >
            <Clock size={14} className="text-emerald-400" />
            <span className="hidden lg:inline">Versões ({snapshots.length})</span>
          </button>

          {/* Compartilhar Link Público */}
          <button
            type="button"
            onClick={handleOpenShare}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/80 hover:bg-white/10 hover:text-white transition-all"
            title="Compartilhar Link Público"
          >
            <Share2 size={14} className="text-sky-400" />
            <span className="hidden lg:inline">Compartilhar</span>
          </button>

          {/* Exportar PNG */}
          <button
            type="button"
            onClick={handleExportPng}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white transition-all"
            title="Exportar Imagem PNG em Alta Resolução"
          >
            <Camera size={14} />
          </button>

          {/* Exportar JSON */}
          <button
            type="button"
            onClick={handleExportJson}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white transition-all"
            title="Exportar Arquivo Astral JSON"
          >
            <FileDown size={14} />
          </button>

          {/* Modo Tela Cheia (Zen) */}
          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-all ${
              isFullscreen
                ? "border-indigo-500 bg-indigo-500/20 text-white"
                : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
            }`}
            title={isFullscreen ? "Sair da Tela Cheia (F ou Esc)" : "Modo Foco Zen (F)"}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </header>

      {/* ------------------------------------------------------------- */}
      {/* CANVAS PRINCIPAL ASTRAL */}
      {/* ------------------------------------------------------------- */}
      <div className="relative flex-1">
        <AstralCanvas
          mapData={activeMap}
          onChangeMapData={handleUpdateMapData}
        />

        {/* Gaveta de Aparência */}
        <AstralAppearanceDrawer
          isOpen={isAppearanceOpen}
          onClose={() => setIsAppearanceOpen(false)}
          layout={activeMap.layout}
          onSelectLayout={(layout) => handleUpdateMapData({ ...activeMap, layout })}
          theme={activeMap.theme}
          onSelectTheme={(theme) => handleUpdateMapData({ ...activeMap, theme })}
          shape={activeMap.defaultNodeShape || "rounded"}
          onSelectShape={(defaultNodeShape) => handleUpdateMapData({ ...activeMap, defaultNodeShape })}
        />

        {/* Modal de Modelos / Templates */}
        <AstralTemplatesModal
          isOpen={isTemplatesModalOpen}
          onClose={() => setIsTemplatesModalOpen(false)}
          onSelectTemplate={handleSelectTemplate}
        />

        {/* Modal / Gaveta de Histórico de Versões */}
        {isSnapshotsOpen && (
          <aside className="absolute right-4 top-4 z-50 flex w-80 max-h-[calc(100vh-100px)] flex-col rounded-2xl border border-white/10 bg-[#111622]/95 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-white/10 p-4">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-emerald-400" />
                <h3 className="text-sm font-bold text-white">Histórico de Versões</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsSnapshotsOpen(false)}
                className="rounded-full p-1 text-white/50 hover:bg-white/10 hover:text-white"
              >
                <X size={15} />
              </button>
            </div>

            {/* Criar Versão Manual */}
            <div className="border-b border-white/10 p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nome do checkpoint (ex: Versão Final)"
                  value={versionLabel}
                  onChange={(e) => setVersionLabel(e.target.value)}
                  className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white placeholder-white/30 focus:border-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleCreateSnapshot}
                  disabled={isSavingSnapshot || !versionLabel.trim()}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
                >
                  {isSavingSnapshot ? "..." : "Salvar"}
                </button>
              </div>
            </div>

            {/* Lista de Versões */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
              {snapshots.length === 0 ? (
                <div className="py-8 text-center text-xs text-white/40">Nenhuma versão salva ainda.</div>
              ) : (
                snapshots.map((snap) => (
                  <div
                    key={snap.id}
                    className="flex items-center justify-between rounded-lg border border-white/5 bg-white/5 p-3 hover:border-white/20 transition-all"
                  >
                    <div>
                      <strong className="block text-xs font-semibold text-white">{snap.label}</strong>
                      <span className="text-[11px] text-white/40">
                        {new Date(snap.createdAt).toLocaleString("pt-BR")} • {snap.nodeCount} tópicos
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRestoreSnapshot(snap.id)}
                      className="rounded bg-white/10 px-2 py-1 text-[11px] font-medium text-white/80 hover:bg-white/20 hover:text-white"
                      title="Restaurar esta versão"
                    >
                      Restaurar
                    </button>
                  </div>
                ))
              )}
            </div>
          </aside>
        )}

        {/* Modal de Compartilhamento de Alta Fidelidade */}
        <AstralShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          mapId={effectiveMapId}
          mapTitle={title}
        />
      </div>

      {/* Toast Notifier */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-xl px-4 py-2.5 text-xs font-semibold shadow-2xl backdrop-blur-md animate-fade-in ${
            toast.type === "success"
              ? "bg-emerald-600/90 text-white"
              : toast.type === "error"
              ? "bg-rose-600/90 text-white"
              : "bg-indigo-600/90 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
